import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import shortid from 'shortid';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import bindMethods from 'auto-bind';
import { setLightness } from 'polished';

import { WHITE } from '../../../constants/colors';
import { COLOR_THEME } from '../../../utils/themer';
import { FOOTBALL_DOWN_DISTANCE } from '../../../constants/fields';
import { DOWN_AND_DISTANCE_KEY, getFilters } from '../../../state/selectors/filters';
import { sortDownDistance } from '../../../utils/sorting';
import {
  toggleDownDistanceCollapse,
  setPossibleSelections,
  setSelections,
  clearPossibleSelections,
} from '../../../state/downDistance';
import { getQlikApp, createHyperCubeDefForField } from '../../../utils/qlik';
import { getQlikAppId } from '../../../state/selectors/getQlikAppId';

export const FilterPaneContainer = styled.div`
  margin-bottom: 32px;

  & h1 {
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
  }
`;

export const FilterHeadline = styled.div`
  padding: 0px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${setLightness(0.65, WHITE)};
`;

export const ListContainer = styled.ul`
  height: ${props => (props.isCollapsed ? '0px' : '100%')};
  overflow: ${props => (props.isCollapsed ? 'hidden' : 'visible')};
`;

export const ListItem = styled.li`
  margin: 2px 0px;
  padding: 12px 30px;
  font-size: 14px;
  font-weight: ${props => (props.isSelected ? 'bold' : 'normal')};
  background: ${props => (props.isSelected ? setLightness(0.25, COLOR_THEME.primary) : '')};
  border-left: ${props => (props.isSelected ? `5px solid ${COLOR_THEME.secondary}` : '')};
  cursor: pointer;
`;

class DownDistanceFilter extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);

    this.hyperCubeDef = createHyperCubeDefForField(FOOTBALL_DOWN_DISTANCE);
  }

  componentDidMount() {
    const { qlikAppId } = this.props;
    if (qlikAppId) {
      this.getPossibleSelections();
    }
  }

  componentDidUpdate(prevProps) {
    const { qlikAppId, clearPossibleSelections } = this.props;
    if (!qlikAppId && prevProps.qlikAppId !== qlikAppId) {
      clearPossibleSelections();
    }

    if (qlikAppId && prevProps.qlikAppId !== qlikAppId) {
      this.getPossibleSelections();
    }
  }

  async getPossibleSelections() {
    const { qlikAppId, setPossibleSelections } = this.props;
    const app = await getQlikApp(qlikAppId);
    const results = await app.createCube(this.hyperCubeDef);

    const possibleSelections = results.layout.qHyperCube.qDataPages[0].qMatrix.map(x => x[0].qText);

    setPossibleSelections(possibleSelections);
  }

  handleListItemClick(item, event) {
    const { selections, setSelections } = this.props;
    const { name } = event.target.dataset;

    let updatedSelections = {};

    if (selections.hasOwnProperty(name)) {
      updatedSelections = { ...selections };
      delete updatedSelections[name];
    } else {
      updatedSelections = {
        ...selections,
        [name]: {
          down: item.operand1Value,
          min: item.operand2Min,
          max: item.operand2Max,
        },
      };
    }
    setSelections(updatedSelections);
  }

  sortFilters() {
    const { filters } = this.props;
    return sortDownDistance(filters);
  }

  render() {
    const {
      t, isCollapsed, selections, toggleDownDistanceCollapse,
    } = this.props;

    return (
      <FilterPaneContainer>
        <FilterHeadline onClick={toggleDownDistanceCollapse}>
          <h1>{t('Sidebar.DownDistance')}</h1>
          <FontAwesomeIcon size="lg" icon={isCollapsed ? faCaretUp : faCaretDown} />
        </FilterHeadline>

        <ListContainer id="Sidebar.DownDistance" isCollapsed={isCollapsed}>
          {this.sortFilters().map((d, k) => (
            <ListItem
              id={`Sidebar.DownDistance.option-${k}`}
              key={shortid.generate()}
              data-name={d.name}
              onClick={this.handleListItemClick.bind(null, d)}
              isSelected={selections.hasOwnProperty(d.name)}
            >
              {d.name}
            </ListItem>
          ))}
        </ListContainer>
      </FilterPaneContainer>
    );
  }
}

DownDistanceFilter.propTypes = {
  qlikAppId: PropTypes.string.isRequired, // supplied from Redux
  isCollapsed: PropTypes.bool.isRequired, // supplied from Redux
  // eslint-disable-next-line react/no-unused-prop-types
  possibleSelections: PropTypes.array.isRequired, // supplied from Redux
  toggleDownDistanceCollapse: PropTypes.func.isRequired, // supplied from Redux
  setPossibleSelections: PropTypes.func.isRequired, // supplied from Redux
  setSelections: PropTypes.func.isRequired, // supplied from Redux
  clearPossibleSelections: PropTypes.func.isRequired, // supplied from Redux
  t: PropTypes.func.isRequired, // supplied from withTranslation
  selections: PropTypes.object.isRequired, // supplied from Redux
  filters: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  qlikAppId: getQlikAppId(state),
  qlikToken: state.auth.qlikToken,
  isCollapsed: state.football.downDistance.isCollapsed,
  possibleSelections: state.football.downDistance.possibleSelections,
  selections: state.football.downDistance.selections,
  filters: getFilters(DOWN_AND_DISTANCE_KEY, state.filters),
});

const mapDispatchToProps = {
  toggleDownDistanceCollapse,
  setPossibleSelections,
  setSelections,
  clearPossibleSelections,
};

const TranslatedComponent = withTranslation()(DownDistanceFilter);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
